import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowDown, CircleCheck } from 'lucide-react';
import { api } from '../lib/api';
import { errorMessage } from '../lib/auth';
import { dateTime, longDate } from '../lib/labels';
import { useQuery } from '../lib/useQuery';

/**
 * Consent, given by the worker on their own phone.
 *
 * This is a legal instrument, not a checkbox. Four things follow from that:
 *
 *   · the notice comes from the server, versioned. It used to be a constant in
 *     this file while the API recorded `templateVersion: 1` regardless — so
 *     the record proving what a worker agreed to pointed at a text that
 *     existed nowhere, and editing this component silently rewrote the meaning
 *     of every consent already given.
 *   · the version and language that were DISPLAYED are submitted back, and the
 *     server refuses a mismatch rather than correcting it. Recording agreement
 *     to a notice the worker never saw is the failure this prevents.
 *   · the notice is shown in full before the button is reachable. A consent
 *     flow that lets someone agree without scrolling is consent to nothing.
 *   · declining is a real option, presented as plainly as agreeing. A screen
 *     with only an "I agree" button is not asking a question.
 *
 * And a fifth: the worker is TOLD it was recorded. Agreeing used to drop them
 * back on Home with nothing said — the one card that had asked for the consent
 * simply was not there any more. Somebody who has just signed a legal notice
 * on a phone, on a factory's mobile signal, is owed a plain "it went through",
 * with the time, before anything else happens.
 */

interface Template {
  code: string;
  version: number;
  /** What was actually served — not necessarily what was asked for. */
  servedLocale: string;
  fellBack: boolean;
  title: string;
  body: string;
  agreeLabel: string;
  declineLabel: string;
}

interface NoticeResponse {
  order: { id: string; examinationType: string; status: string; consentRequired: boolean };
  template: Template;
  availableLocales: string[];
  alreadyRecorded: { id: string; acceptedAt: string; locale: string } | null;
}

const LOCALE_NAME: Record<string, string> = {
  en: 'English', hi: 'हिन्दी', gu: 'ગુજરાતી',
};

export function Consent() {
  const { orderId } = useParams<{ orderId: string }>();
  const nav = useNavigate();

  // Starts in the worker's own language, as HR recorded it; the picker
  // still overrides. It used to start in English for everybody.
  const [locale, setLocale] = useState<string | null>(null);
  const profile = useQuery<{ preferredLocale?: string | null }>(() => api.get('/me/profile'), []);
  const wanted = locale ?? profile.data?.preferredLocale ?? 'en';
  const [readToEnd, setReadToEnd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [declined, setDeclined] = useState(false);
  // Set once the server has answered: what was agreed to, and when.
  const [recorded, setRecorded] = useState<{ at: string; version: number; locale: string } | null>(null);

  const notice = useQuery<NoticeResponse>(
    () => (profile.loading && !locale
      ? new Promise<NoticeResponse>(() => undefined)
      : api.get(`/me/examinations/${orderId}/consent?locale=${encodeURIComponent(wanted)}`)),
    [orderId, wanted, profile.loading],
  );

  const t = notice.data?.template;

  // A notice short enough to fit its box has no scroll event to fire, so the
  // gate below never opened and the worker could neither agree nor tell why.
  // Checked whenever the text changes (a new language, a re-fetch) and when
  // the phone is rotated.
  const bodyRef = useRef<HTMLDivElement>(null);
  const checkReadToEnd = useCallback(() => {
    const el = bodyRef.current;
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 24) setReadToEnd(true);
  }, []);
  useEffect(() => {
    checkReadToEnd();
    window.addEventListener('resize', checkReadToEnd);
    return () => window.removeEventListener('resize', checkReadToEnd);
  }, [notice.data, checkReadToEnd]);

  async function agree() {
    if (!t) return;
    setBusy(true);
    setError(null);
    try {
      // The identity of the notice ON SCREEN, not the language the picker is
      // set to. Where a language has no published notice the server serves
      // English, and English is then what the worker read.
      await api.post(`/me/consents/${orderId}`, {
        locale: t.servedLocale,
        templateCode: t.code,
        templateVersion: t.version,
      });
      // Only now, and only because the server said so. The confirmation names
      // the notice that was on screen — the one the record carries.
      setRecorded({ at: new Date().toISOString(), version: t.version, locale: t.servedLocale });
    } catch (e) {
      setError(errorMessage(e));
      // A stale notice is the one error with a specific recovery: re-read.
      // Refetching also clears the scroll gate, so the worker cannot agree to
      // the new text without having seen it.
      notice.reload();
      setReadToEnd(false);
    } finally {
      setBusy(false);
    }
  }

  if (recorded) {
    return (
      <div className="space-y-4 py-4">
        <div
          role="status"
          className="rounded-xl border border-success-600/30 bg-success-100 px-5 py-6 text-center"
        >
          <CircleCheck aria-hidden className="mx-auto h-10 w-10 text-success-600" strokeWidth={2} />
          <h1 className="mt-2 text-lg font-semibold text-neutral-900">Your consent has been recorded</h1>
          <p className="mt-1 text-sm text-neutral-800">
            {dateTime(recorded.at)}
          </p>
        </div>
        <p className="text-sm text-neutral-700">
          You agreed to your health checkup. It can now go ahead — there is nothing more to do on
          this screen.
        </p>
        <p className="text-xs text-neutral-600">
          Recorded against notice version {recorded.version} · {LOCALE_NAME[recorded.locale] ?? recorded.locale}.
          You can ask your occupational health team to see this record at any time.
        </p>
        <button
          onClick={() => nav('/home', { replace: true })}
          className="min-h-12 w-full rounded-lg bg-primary-600 py-3 text-base font-medium text-white active:bg-primary-700"
        >
          Back to home
        </button>
      </div>
    );
  }

  if (declined) {
    return (
      <div className="space-y-4 py-4">
        <h1 className="text-base font-semibold text-neutral-900">You have not agreed</h1>
        <p className="text-sm text-neutral-700">
          Your checkup will not go ahead without your consent. This examination is required by
          law for your job, so please speak to your HR or occupational health team about it.
        </p>
        <p className="text-sm text-neutral-600">
          Nothing has been recorded. You can come back to this screen at any time.
        </p>
        <button
          onClick={() => setDeclined(false)}
          className="min-h-12 w-full rounded-lg border border-neutral-300 py-3 text-base font-medium text-neutral-800 active:bg-neutral-100"
        >
          Read the notice again
        </button>
        <button
          onClick={() => nav('/home', { replace: true })}
          className="min-h-12 w-full rounded-lg py-3 text-sm font-medium text-primary-700 underline underline-offset-2 active:bg-neutral-100"
        >
          Back to home
        </button>
      </div>
    );
  }

  if (notice.loading && !t) {
    return (
      <div className="space-y-3 py-2" role="status" aria-label="Loading the consent notice">
        <div className="h-5 w-2/3 animate-pulse rounded bg-neutral-100" />
        <div className="h-72 animate-pulse rounded-xl bg-neutral-100" />
      </div>
    );
  }

  if (notice.error && !t) {
    return (
      <div className="space-y-4 py-6">
        <h1 className="text-base font-semibold text-neutral-900">Consent for your checkup</h1>
        <div role="alert" className="rounded-lg bg-danger-100 px-4 py-3 text-sm text-danger-600">
          {notice.error}
        </div>
        <button
          onClick={notice.reload}
          className="min-h-12 w-full rounded-lg border border-neutral-300 py-3 text-sm font-medium text-neutral-800 active:bg-neutral-100"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!t) return null;

  if (notice.data?.alreadyRecorded) {
    return (
      <div className="space-y-4 py-4">
        <h1 className="text-base font-semibold text-neutral-900">You have already agreed</h1>
        <p className="text-sm text-neutral-700">
          Your consent was recorded on {longDate(notice.data.alreadyRecorded.acceptedAt)}. There is
          nothing more to do here.
        </p>
        <button
          onClick={() => nav('/home', { replace: true })}
          className="min-h-12 w-full rounded-lg bg-primary-600 py-3 text-base font-medium text-white active:bg-primary-700"
        >
          Back to home
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 py-2">
      <div role="group" aria-label="Language of the notice" className="flex flex-wrap gap-2">
        {(notice.data?.availableLocales ?? ['en']).map((l) => (
          <button
            key={l}
            lang={l}
            aria-pressed={t.servedLocale === l}
            onClick={() => { setLocale(l); setReadToEnd(false); }}
            className={`min-h-10 rounded-full px-4 text-sm font-medium ${
              t.servedLocale === l
                ? 'bg-primary-600 text-white'
                : 'border border-neutral-300 bg-white text-neutral-800 active:bg-neutral-100'
            }`}
          >
            {LOCALE_NAME[l] ?? l}
          </button>
        ))}
      </div>

      {/* Said plainly rather than swapped silently: a worker who picked
          Gujarati and is reading English should know that is what happened. */}
      {t.fellBack && (
        <p className="rounded-lg bg-neutral-100 px-3 py-2 text-xs text-neutral-700">
          This notice is not yet available in that language, so it is shown in English.
        </p>
      )}

      <h1 className="text-base font-semibold text-neutral-900">{t.title}</h1>

      <div
        ref={bodyRef}
        // The button below unlocks only once this has been scrolled to the
        // end. Agreeing to a notice you could not have read is not consent.
        onScroll={checkReadToEnd}
        // Focusable and named, so the notice can be scrolled from a keyboard
        // or a switch — otherwise the gate below is closed to anyone who
        // does not scroll by touch.
        tabIndex={0}
        role="region"
        aria-label="Consent notice"
        lang={t.servedLocale}
        // As tall as the screen allows below the buttons, never less than
        // 18rem: on a tall phone the whole notice often fits, and a notice
        // that fits has nothing to scroll — the gate opens at once.
        className="max-h-[max(18rem,calc(100dvh_-_26rem))] space-y-3 overflow-y-auto rounded-xl border border-neutral-200 bg-white p-4 text-base leading-relaxed text-neutral-800"
      >
        {/* Paragraph breaks are presentational; the stored text is the legal
            artifact and is rendered exactly as it is held. */}
        {t.body.split('\n\n').map((para, i) => <p key={i}>{para.trim()}</p>)}
      </div>

      {error && (
        <div role="alert" className="rounded-lg bg-danger-100 px-4 py-3 text-sm text-danger-600">
          {error}
        </div>
      )}

      {/* Said at the button that is waiting for it. The hint was a small grey
          line between the notice and the buttons, and a disabled "I agree"
          read as broken (flow audit 2026-10-20, 6.2). */}
      <div>
        <p
          id="consent-gate"
          aria-live="polite"
          className={readToEnd
            ? 'mb-2 text-center text-xs text-neutral-600'
            : 'mb-2 flex items-center justify-center gap-1.5 rounded-lg bg-warning-100 px-3 py-2 text-sm font-medium text-warning-600'}
        >
          {readToEnd ? 'You have reached the end of the notice.' : (
            <><ArrowDown aria-hidden className="h-4 w-4 shrink-0" />Scroll the notice above to the end to agree</>
          )}
        </p>
        <button
          onClick={agree}
          disabled={!readToEnd || busy}
          aria-describedby="consent-gate"
          className="min-h-12 w-full rounded-lg bg-primary-600 py-3 text-base font-medium text-white
                     active:bg-primary-700 disabled:bg-neutral-200 disabled:text-neutral-500"
        >
          {busy ? 'Recording…' : t.agreeLabel}
        </button>
      </div>

      {/* Declining is presented as plainly as agreeing. A screen with only an
          "I agree" button is not asking a question. */}
      <button
        onClick={() => setDeclined(true)}
        disabled={busy}
        className="min-h-12 w-full rounded-lg border border-neutral-300 bg-white py-3 text-base font-medium text-neutral-800 active:bg-neutral-100"
      >
        {t.declineLabel}
      </button>

      {/* The version on screen, so a worker or an auditor can see which notice
          this consent was given against. */}
      <p className="pt-1 text-center text-xs text-neutral-500">
        Notice version {t.version} · {LOCALE_NAME[t.servedLocale] ?? t.servedLocale}
      </p>
    </div>
  );
}
